
import React, { useState, useEffect, useRef } from 'react';
import { Button } from "primereact/button";
import { Toast } from 'primereact/toast';
import { auth } from "../../config/firebase";
import axios from 'axios';
import '../../styles/Cooking.css';

const RecipeCard = ({ recipe, onClick }) => {
    const [isBookmarked, setIsBookmarked] = useState(recipe.isPartnerRecipe || false);
    const [loading, setLoading] = useState(false);
    const toast = useRef(null);

    useEffect(() => {
        // If it's a partner recipe, it's already bookmarked
        if (recipe.isPartnerRecipe) {
            return;
        }

        const checkIfBookmarked = async () => {
            const currentUser = auth.currentUser;
            if (currentUser && recipe.id) {
                try {
                    // Check if recipe is already in favorites using the backend API
                    const response = await axios.get(`https://api.frazza.online/recipes/check-favorite/${recipe.id}`, {
                        params: { userId: currentUser.uid }
                    });
                    setIsBookmarked(response.data.isBookmarked);
                } catch (error) {
                    console.error('Error checking if recipe is bookmarked:', error);
                    setIsBookmarked(false);
                }
            }
        };

        checkIfBookmarked();
    }, [recipe.id, recipe.isPartnerRecipe]);

    const handleBookmark = async (e) => {
        e.stopPropagation(); // Prevent card click

        // Prevent unbookmarking partner recipes
        if (recipe.isPartnerRecipe) {
            toast.current.show({ 
                severity: 'info', 
                summary: 'Partner Recipe', 
                detail: 'This recipe is from your partner\'s favorites and cannot be unbookmarked' 
            });
            return;
        }

        setLoading(true);

        try {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'You must be logged in to bookmark recipes' });
                return;
            }

            const recipeData = {
                id: recipe.id,
                title: recipe.title,
                image: recipe.image || '',
                ingredients: recipe.ingredients || [],
                instructions: recipe.instructions || '',
                servings: recipe.servings || '',
                prepTime: recipe.prepTime || '',
                cookTime: recipe.cookTime || '',
                healthLabels: recipe.healthLabels || [],
                dietLabels: recipe.dietLabels || [],
                savedAt: new Date().toISOString()
            };

            const userUid = currentUser.uid;

            if (isBookmarked) {
                // Remove bookmark using the backend API
                await axios.delete(`https://api.frazza.online/recipes/favorites/${recipe.id}`, {
                    params: { userId: userUid }
                });
                setIsBookmarked(false);
            } else {
                // Add bookmark using the backend API
                await axios.post(`https://api.frazza.online/recipes/favorites`, {
                    userId: userUid,
                    recipe: recipeData
                });
                setIsBookmarked(true);
            }
        } catch (error) {
            console.error('Error bookmarking recipe:', error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to bookmark recipe' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <div className="recipe-card minimal" onClick={() => onClick(recipe.id)}>
                <div className="recipe-card-img-container minimal">
                    <img
                        src={recipe.image || 'https://via.placeholder.com/312x231?text=No+Image'}
                        alt={recipe.title}
                        className="recipe-image minimal"
                    />
                    <div className="recipe-card-gradient-overlay minimal" />
                    <div className="recipe-card-title minimal">{recipe.title}</div>
                    <Button
                        onClick={handleBookmark}
                        className={`recipe-bookmark-btn minimal ${isBookmarked ? "p-button-success" : "p-button-secondary"}`}
                        loading={loading}
                        tooltip={isBookmarked ? 'Remove Bookmark' : 'Bookmark'}
                        tooltipOptions={{ position: 'top' }}
                        onClickCapture={e => e.stopPropagation()}
                    >
                        <i className={`pi ${isBookmarked ? "pi-bookmark-fill" : "pi-bookmark"}`}></i>
                    </Button>
                </div>
            </div>
        </>
    );
};

export default RecipeCard;
